import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { templatesAPI, formsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Search, CheckCircle, Zap, FileText, Eye, X, ChevronRight,
  Users, Briefcase, Calendar, PartyPopper, UserCheck, ShoppingCart,
  MessageSquare, GraduationCap, Monitor, ChevronDown, Brain
} from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

// Category icons and colors (Quiz is now a category)
const categoryConfig = {
  'Popular': { icon: Zap, color: 'bg-amber-500', textColor: 'text-amber-600' },
  'Contact & Leads': { icon: Users, color: 'bg-blue-500', textColor: 'text-blue-600' },
  'Business & Office': { icon: Briefcase, color: 'bg-purple-500', textColor: 'text-purple-600' },
  'Booking & Scheduling': { icon: Calendar, color: 'bg-green-500', textColor: 'text-green-600' },
  'Events': { icon: PartyPopper, color: 'bg-pink-500', textColor: 'text-pink-600' },
  'HR & Jobs': { icon: UserCheck, color: 'bg-orange-500', textColor: 'text-orange-600' },
  'Orders & Payments': { icon: ShoppingCart, color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  'Feedback & Surveys': { icon: MessageSquare, color: 'bg-cyan-500', textColor: 'text-cyan-600' },
  'Education': { icon: GraduationCap, color: 'bg-indigo-500', textColor: 'text-indigo-600' },
  'IT & Technical': { icon: Monitor, color: 'bg-slate-500', textColor: 'text-slate-600' },
  // Quiz category
  'Quiz & Assessment': { icon: Brain, color: 'bg-gradient-to-r from-purple-500 to-orange-500', textColor: 'text-purple-600' },
};

// Mini form preview component - JotForm-style sectioned design
const FormPreviewCard = ({ template, onPreview, onUse }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Group fields by sections (headings create sections)
  const getSections = () => {
    const sections = [];
    let currentSection = { title: null, fields: [] };
    
    template.fields.forEach((field, idx) => {
      if (field.type === 'heading') {
        if (currentSection.fields.length > 0 || currentSection.title) {
          sections.push(currentSection);
        }
        currentSection = { title: field.label, fields: [] };
      } else if (field.type !== 'divider' && field.type !== 'html' && field.type !== 'captcha') {
        currentSection.fields.push(field);
      }
    });
    
    if (currentSection.fields.length > 0 || currentSection.title) {
      sections.push(currentSection);
    }
    
    // If no sections, create one with all fields
    if (sections.length === 0) {
      sections.push({ title: null, fields: template.fields.filter(f => 
        !['heading', 'divider', 'html', 'captcha'].includes(f.type)
      )});
    }
    
    return sections.slice(0, 3); // Max 3 sections in preview
  };
  
  // Render a field preview
  const renderFieldPreview = (field) => {
    const isNameField = field.type === 'name';
    const isAddressField = field.type === 'address';
    
    if (isNameField) {
      return (
        <div key={field.id} className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[8px] text-slate-500 mb-0.5 font-medium">First Name</p>
            <div className="h-4 bg-white rounded border border-slate-200" />
          </div>
          <div>
            <p className="text-[8px] text-slate-500 mb-0.5 font-medium">Last Name</p>
            <div className="h-4 bg-white rounded border border-slate-200" />
          </div>
        </div>
      );
    }
    
    if (isAddressField) {
      return (
        <div key={field.id} className="space-y-1.5">
          <div>
            <p className="text-[8px] text-slate-500 mb-0.5 font-medium">Street Address</p>
            <div className="h-4 bg-white rounded border border-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[8px] text-slate-500 mb-0.5 font-medium">City</p>
              <div className="h-4 bg-white rounded border border-slate-200" />
            </div>
            <div>
              <p className="text-[8px] text-slate-500 mb-0.5 font-medium">State</p>
              <div className="h-4 bg-white rounded border border-slate-200" />
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div key={field.id}>
        <p className="text-[8px] text-slate-500 mb-0.5 truncate font-medium">{field.label}</p>
        {field.type === 'textarea' ? (
          <div className="h-8 bg-white rounded border border-slate-200" />
        ) : field.type === 'select' ? (
          <div className="h-4 bg-white rounded border border-slate-200 flex items-center px-1.5">
            <span className="text-[7px] text-slate-400">Select...</span>
            <ChevronDown className="w-2 h-2 text-slate-400 ml-auto" />
          </div>
        ) : (field.type === 'radio' || field.type === 'image_choice') ? (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {(field.options || field.image_options || ['Option 1', 'Option 2']).slice(0, 3).map((opt, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 border border-slate-300 rounded-full" />
                <span className="text-[7px] text-slate-500">{typeof opt === 'string' ? opt.slice(0, 12) : opt.label?.slice(0, 12)}</span>
              </div>
            ))}
          </div>
        ) : (field.type === 'checkbox' || field.type === 'multiple') ? (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {(field.options || []).slice(0, 3).map((opt, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 border border-slate-300 rounded-sm" />
                <span className="text-[7px] text-slate-500">{opt.slice(0, 12)}</span>
              </div>
            ))}
          </div>
        ) : field.type === 'date' ? (
          <div className="h-4 bg-white rounded border border-slate-200 flex items-center px-1.5">
            <span className="text-[7px] text-slate-400">MM/DD/YYYY</span>
          </div>
        ) : field.type === 'time' ? (
          <div className="h-4 bg-white rounded border border-slate-200 flex items-center px-1.5">
            <span className="text-[7px] text-slate-400">HH:MM</span>
          </div>
        ) : field.type === 'signature' ? (
          <div className="h-10 bg-white rounded border border-slate-200 border-dashed flex items-center justify-center">
            <span className="text-[7px] text-slate-400">Sign here</span>
          </div>
        ) : (
          <div className="h-4 bg-white rounded border border-slate-200" />
        )}
      </div>
    );
  };

  const config = categoryConfig[template.category] || { color: 'bg-slate-500', textColor: 'text-slate-600' };
  const sections = getSections();
  const isQuiz = template.template_type === 'quiz';

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-slate-300 transition-all duration-300 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`template-card-${template.id}`}
    >
      {/* Form Preview Area */}
      <div className="relative h-80 bg-gradient-to-b from-slate-50 to-slate-100/50 p-3 overflow-hidden">
        {/* Mini form preview - JotForm style */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm h-full overflow-hidden flex flex-col">
          {/* Form Header */}
          <div className={`px-3 py-2.5 ${isQuiz ? 'bg-gradient-to-r from-purple-500 to-orange-500' : 'bg-indigo-600'} flex-shrink-0`}>
            <p className="text-[10px] font-bold text-white text-center truncate">{template.name}</p>
          </div>
          
          {/* Form Body - Sections */}
          <div className="p-2.5 flex-1 flex flex-col justify-between overflow-hidden">
            <div className="space-y-3 flex-1">
              {sections.map((section, sIdx) => (
                <div key={sIdx} className="space-y-1.5">
                  {/* Section Header */}
                  {section.title && (
                    <div className="border-b border-slate-100 pb-1">
                      <p className="text-[9px] font-semibold text-slate-700">{section.title}</p>
                    </div>
                  )}
                  
                  {/* Section Fields */}
                  <div className="space-y-2">
                    {section.fields.slice(0, 8).map((field) => renderFieldPreview(field))}
                    {section.fields.length > 8 && (
                      <p className="text-[5px] text-slate-400">+{section.fields.length - 8} more</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Submit Button Preview - at bottom */}
            <div className="pt-2">
              <div className={`h-5 ${isQuiz ? 'bg-gradient-to-r from-purple-500 to-orange-500' : 'bg-indigo-600'} rounded text-[7px] text-white flex items-center justify-center font-medium`}>
                {isQuiz ? 'Submit Quiz' : 'Submit'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Quiz Badge */}
        {isQuiz && (
          <div className="absolute top-5 right-5">
            <Badge className="bg-gradient-to-r from-purple-500 to-orange-500 text-white text-[10px] px-2 py-0.5">
              Quiz
            </Badge>
          </div>
        )}
        
        {/* Preview overlay on hover */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent flex items-center justify-center transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col gap-2">
            <Button 
              size="sm" 
              className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg"
              onClick={() => onPreview(template)}
              data-testid={`preview-btn-${template.id}`}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </div>
        </div>
      </div>
      
      {/* Template Info */}
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1 group-hover:text-indigo-600 transition-colors">
          {template.name}
        </h3>
        <p className="text-sm text-slate-500 mb-3 line-clamp-2 h-10">
          {template.description}
        </p>
        <Badge variant="secondary" className={`${config.textColor} bg-opacity-10 mb-3`}>
          {template.category}
        </Badge>
        <Button 
          className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors"
          onClick={() => onUse(template)}
          data-testid={`use-template-btn-${template.id}`}
        >
          Use Template
        </Button>
      </div>
    </div>
  );
};

// Full form preview in dialog
const FormPreviewDialog = ({ template, open, onClose, onUse }) => {
  if (!template) return null;

  const renderField = (field) => {
    switch (field.type) {
      case 'heading':
        return (
          <div key={field.id} className="col-span-2 mt-4 mb-2">
            <h3 className="text-base font-semibold text-slate-800 border-b pb-2">{field.label}</h3>
          </div>
        );
      case 'textarea':
        return (
          <div key={field.id} className="col-span-2">
            <Label className="text-sm text-slate-700">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
            <Textarea placeholder={field.placeholder} disabled className="mt-1 bg-white" rows={3} />
          </div>
        );
      case 'select':
        return (
          <div key={field.id}>
            <Label className="text-sm text-slate-700">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
            <Select disabled>
              <SelectTrigger className="mt-1 bg-white">
                <SelectValue placeholder={field.placeholder || "Select..."} />
              </SelectTrigger>
            </Select>
          </div>
        );
      case 'radio':
        return (
          <div key={field.id} className="col-span-2">
            <Label className="text-sm text-slate-700 mb-2 block">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
            <div className="flex flex-wrap gap-3">
              {(field.options || []).map((opt, i) => (
                <label key={i} className="flex items-center gap-2">
                  <input type="radio" disabled className="w-4 h-4" />
                  <span className="text-sm text-slate-600">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 'checkbox':
      case 'multiple':
        return (
          <div key={field.id} className="col-span-2">
            <Label className="text-sm text-slate-700 mb-2 block">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
            <div className="flex flex-wrap gap-3">
              {(field.options || []).map((opt, i) => (
                <label key={i} className="flex items-center gap-2">
                  <input type="checkbox" disabled className="w-4 h-4 rounded" />
                  <span className="text-sm text-slate-600">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div key={field.id}>
            <Label className="text-sm text-slate-700">{field.label}{field.required && <span className="text-red-500">*</span>}</Label>
            <Input 
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} 
              placeholder={field.placeholder} 
              disabled 
              className="mt-1 bg-white" 
            />
          </div>
        );
    }
  };

  const isQuiz = template.template_type === 'quiz';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Left - Form Preview */}
          <div className="lg:w-3/5 bg-slate-100 p-6 overflow-y-auto max-h-[60vh] lg:max-h-[85vh]">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 max-w-md mx-auto">
              <h2 className="text-xl font-bold text-slate-900 mb-1 text-center">{template.name}</h2>
              {template.description && (
                <p className="text-sm text-slate-500 mb-6 text-center">{template.description}</p>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {template.fields.map(field => renderField(field))}
              </div>
              
              <Button className={`w-full mt-6 ${isQuiz ? 'bg-gradient-to-r from-purple-500 to-orange-500 hover:from-purple-600 hover:to-orange-600' : 'bg-indigo-600 hover:bg-indigo-700'}`} disabled>
                {isQuiz ? 'Submit Quiz' : 'Submit'}
              </Button>
            </div>
          </div>
          
          {/* Right - Template Info */}
          <div className="lg:w-2/5 p-6 bg-white overflow-y-auto">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-4">
              <Input placeholder="Search in all templates" className="w-full" disabled />
            </div>
            
            <div className="text-sm text-slate-500 mb-2">
              Templates <ChevronRight className="w-4 h-4 inline" /> {template.category}
            </div>
            
            <h1 className="text-2xl font-bold text-slate-900 mb-4">{template.name}</h1>
            
            {isQuiz && (
              <Badge className="bg-gradient-to-r from-purple-500 to-orange-500 text-white mb-4">
                Quiz Template
              </Badge>
            )}
            
            <p className="text-slate-600 mb-6">{template.description}</p>
            
            <Button 
              className={`w-full h-12 text-base mb-6 ${isQuiz ? 'bg-gradient-to-r from-purple-500 to-orange-500 hover:from-purple-600 hover:to-orange-600' : 'bg-indigo-600 hover:bg-[#F59E0B]'} text-white`}
              onClick={() => onUse(template)}
              data-testid="dialog-use-template-btn"
            >
              Use Template
            </Button>
            
            <div className="mb-4">
              <h3 className="font-semibold text-slate-900 mb-2">Categories</h3>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-slate-600">{template.category}</Badge>
                {isQuiz && <Badge variant="outline" className="text-purple-600">Quiz</Badge>}
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-slate-900 mb-2">Template Details</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  {template.fields.length} fields included
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Fully customizable
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Mobile responsive
                </li>
                {isQuiz && (
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    Auto-scoring enabled
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortOrder, setSortOrder] = useState('popular');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [signupPrompt, setSignupPrompt] = useState({ open: false, template: null });
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, usage } = useAuth();
  
  useEffect(() => {
    loadTemplates();
  }, []);

  // Read ?category=... from URL on every navigation so deep-links from the
  // landing-page Use Cases section open the right category pre-selected.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      setSelectedCategory(cat);
    }
  }, [location.search]);

  const loadTemplates = async () => {
    try {
      const response = await templatesAPI.getAll();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async (template) => {
    if (!isAuthenticated) {
      // Save template to localStorage and redirect to guest builder
      localStorage.setItem('guest_form_draft', JSON.stringify({
        title: template.name,
        description: template.description,
        fields: template.fields,
        fromTemplate: template.id
      }));
      setPreviewTemplate(null);
      toast.success('Template loaded! Customize your form.');
      navigate('/try-builder');
      return;
    }

    if (usage && !usage.can_create_form) {
      toast.error('You have reached your form limit. Upgrade for more forms.');
      navigate('/pricing');
      return;
    }

    try {
      const response = await formsAPI.create({
        title: template.name,
        description: template.description,
        from_template: template.id
      });
      toast.success('Form created from template!');
      navigate(`/forms/${response.data.id}/builder`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Plan limit reached.');
        navigate('/pricing');
      } else {
        toast.error('Failed to create form');
      }
    }
  };

  // Get categories with counts - include Quiz as a special category
  const categoriesWithCounts = templates.reduce((acc, t) => {
    // Regular category
    acc[t.category] = (acc[t.category] || 0) + 1;
    return acc;
  }, {});
  
  // Count quiz templates separately
  const quizCount = templates.filter(t => t.template_type === 'quiz').length;
  
  // Count popular templates
  const popularCount = templates.filter(t => t.is_popular).length;

  // Get sorted categories (excluding Quiz & Assessment as it's handled specially)
  const sortedCategories = Object.keys(categoriesWithCounts)
    .filter(cat => cat !== 'Quiz & Assessment')
    .sort();
  
  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           (selectedCategory === 'Popular' ? template.is_popular : 
                            selectedCategory === 'Quiz & Assessment' ? template.template_type === 'quiz' :
                            template.category === selectedCategory);
    return matchesSearch && matchesCategory;
  });

  // Sort templates based on sortOrder
  const sortedFilteredTemplates = [...filteredTemplates].sort((a, b) => {
    switch (sortOrder) {
      case 'popular':
        // Popular templates first, then by name
        if (a.is_popular && !b.is_popular) return -1;
        if (!a.is_popular && b.is_popular) return 1;
        return a.name.localeCompare(b.name);
      case 'newest':
        // Sort by created_at descending (newest first)
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'name':
        // Sort alphabetically by name
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  // Get page title based on selected category
  const getPageTitle = () => {
    if (selectedCategory === 'all') return 'All Templates';
    if (selectedCategory === 'Popular') return 'Popular Templates';
    if (selectedCategory === 'Quiz & Assessment') return 'Quiz & Assessment Templates';
    return `${selectedCategory} Templates`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <PublicHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex gap-8">
          {/* Left Sidebar - Categories */}
          <aside className="hidden lg:block w-56 flex-shrink-0 py-6">
            {/* Sort By */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Sort By</p>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-full" data-testid="sort-by-dropdown">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Categories */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Categories</p>
              
              {/* All Templates */}
              <button
                className={`w-full flex items-center justify-between py-2 px-3 rounded-lg mb-1 transition-colors ${
                  selectedCategory === 'all' ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-slate-100 text-slate-700'
                }`}
                onClick={() => setSelectedCategory('all')}
                data-testid="category-all"
              >
                <span className="font-medium text-sm">All Templates</span>
                <span className="text-xs text-slate-400">{templates.length}</span>
              </button>
              
              {/* Popular - Featured Category */}
              {popularCount > 0 && (
                <button
                  className={`w-full flex items-center justify-between py-2 px-3 rounded-lg mb-1 transition-colors ${
                    selectedCategory === 'Popular' ? 'bg-amber-50 text-amber-600' : 'hover:bg-slate-100 text-slate-700'
                  }`}
                  onClick={() => setSelectedCategory('Popular')}
                  data-testid="category-popular"
                >
                  <div className="flex items-center gap-2">
                    <Zap className={`w-4 h-4 ${selectedCategory === 'Popular' ? 'text-amber-600' : 'text-amber-500'}`} />
                    <span className="text-sm font-medium">Popular</span>
                  </div>
                  <span className="text-xs text-slate-400">{popularCount}</span>
                </button>
              )}
              
              {/* Quiz & Assessment - Special Category */}
              {quizCount > 0 && (
                <button
                  className={`w-full flex items-center justify-between py-2 px-3 rounded-lg mb-1 transition-colors ${
                    selectedCategory === 'Quiz & Assessment' ? 'bg-purple-50 text-purple-600' : 'hover:bg-slate-100 text-slate-700'
                  }`}
                  onClick={() => setSelectedCategory('Quiz & Assessment')}
                  data-testid="category-quiz"
                >
                  <div className="flex items-center gap-2">
                    <Brain className={`w-4 h-4 ${selectedCategory === 'Quiz & Assessment' ? 'text-purple-600' : 'text-purple-500'}`} />
                    <span className="text-sm font-medium">Quiz & Assessment</span>
                  </div>
                  <span className="text-xs text-slate-400">{quizCount}</span>
                </button>
              )}
              
              {/* Divider */}
              <div className="border-t border-slate-200 my-3"></div>
              
              {/* Category List */}
              <div className="space-y-0.5">
                {sortedCategories.map(cat => {
                  const config = categoryConfig[cat] || { icon: FileText, color: 'bg-slate-500' };
                  const Icon = config.icon;
                  const isActive = selectedCategory === cat;
                  
                  return (
                    <button
                      key={cat}
                      className={`w-full flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                        isActive ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-slate-100 text-slate-700'
                      }`}
                      onClick={() => setSelectedCategory(cat)}
                      data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                        <span className="text-sm">{cat}</span>
                      </div>
                      <span className="text-xs text-slate-400">{categoriesWithCounts[cat]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 py-6 min-w-0">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search templates..."
                  className="pl-10 bg-white"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="template-search-input"
                />
              </div>
            </div>

            {/* Page Title */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900" data-testid="templates-page-title">
                {getPageTitle()}
              </h1>
              <p className="text-slate-500">
                {sortedFilteredTemplates.length} template{sortedFilteredTemplates.length !== 1 ? 's' : ''} available
              </p>
            </div>

            {/* Mobile Category Filter */}
            <div className="lg:hidden mb-6 overflow-x-auto">
              <div className="flex gap-2 pb-2">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                  data-testid="mobile-category-all"
                >
                  All
                </Button>
                {popularCount > 0 && (
                  <Button
                    variant={selectedCategory === 'Popular' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory('Popular')}
                    className="whitespace-nowrap"
                    data-testid="mobile-category-popular"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Popular
                  </Button>
                )}
                {quizCount > 0 && (
                  <Button
                    variant={selectedCategory === 'Quiz & Assessment' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory('Quiz & Assessment')}
                    className="whitespace-nowrap"
                    data-testid="mobile-category-quiz"
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    Quiz
                  </Button>
                )}
                {sortedCategories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className="whitespace-nowrap"
                    data-testid={`mobile-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>

            {/* Templates Grid */}
            <div>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : sortedFilteredTemplates.length === 0 ? (
                <div className="text-center py-16">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No templates found</h3>
                  <p className="text-slate-600">Try adjusting your search or filter</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="templates-grid">
                  {sortedFilteredTemplates.map(template => (
                    <FormPreviewCard
                      key={template.id}
                      template={template}
                      onPreview={setPreviewTemplate}
                      onUse={handleUseTemplate}
                    />
                  ))}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Preview Dialog */}
      <FormPreviewDialog
        template={previewTemplate}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onUse={handleUseTemplate}
      />

      {/* Signup Prompt Dialog */}
      <Dialog open={signupPrompt.open} onOpenChange={(open) => setSignupPrompt({ open, template: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
              Create a Free Account
            </DialogTitle>
            <DialogDescription>
              Sign up free to use "{signupPrompt.template?.name}" template.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 mt-2">
            <h4 className="font-semibold text-slate-900 mb-2">Free account includes:</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Create up to 3 forms</li>
              <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> 100 submissions/month</li>
              <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> All templates included</li>
              <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Export to CSV</li>
            </ul>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSignupPrompt({ open: false, template: null })}>
              Later
            </Button>
            <Button asChild className="bg-indigo-600 hover:bg-[#F59E0B]">
              <Link to="/signup">
                Sign Up Free
                <Zap className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TemplatesPage;
